import { useEffect, useState } from "react";
import { MyListApi } from "./restApi";
import { getItemImageUrl } from "./itemUtil";

// itemId -> { url } once known, or { promise } while a lookup is in flight.
// Lookups are shared across cards and renders so each item is fetched at most
// once per session (a failed lookup is dropped so a later mount can retry).
const cache = new Map();

export const clearItemImageCache = () => cache.clear();

// Records the default image of an item already in hand (feed items, search
// results) so no lookup is needed for it later.
export const seedItemImage = item => {
    if (item?.id && item?.tag) {
        cache.set(String(item.id), { url: getItemImageUrl(item) });
    }
}

export const getCachedItemImage = itemId => {
    const entry = itemId ? cache.get(String(itemId)) : null;
    return entry && 'url' in entry ? entry.url : undefined;
}

export const resolveItemImageUrl = (itemId, fetchItem = MyListApi.getItem) => {
    const key = String(itemId);
    const entry = cache.get(key);
    if (entry) return 'url' in entry ? Promise.resolve(entry.url) : entry.promise;
    const promise = Promise.resolve()
        .then(() => fetchItem(itemId))
        .then(item => {
            const url = getItemImageUrl(item);
            cache.set(key, { url });
            return url;
        })
        .catch(() => {
            cache.delete(key);
            return null;
        });
    cache.set(key, { promise });
    return promise;
}

// Resolves an item's default image URL (null when it has none) only while
// `enabled`, e.g. when there is no experience photo or it failed to load.
export const useItemImageUrl = (itemId, enabled = true) => {
    const [url, setUrl] = useState(() => (enabled ? getCachedItemImage(itemId) : undefined));

    useEffect(() => {
        if (!enabled || !itemId) {
            setUrl(undefined);
            return undefined;
        }
        const cached = getCachedItemImage(itemId);
        if (cached !== undefined) {
            setUrl(cached);
            return undefined;
        }
        let cancelled = false;
        resolveItemImageUrl(itemId).then(resolved => {
            if (!cancelled) setUrl(resolved);
        });
        return () => { cancelled = true; };
    }, [itemId, enabled]);

    return url;
}
